import { Flex, Text } from '@chakra-ui/react';
import _ from 'lodash';
import React, { useMemo, useState } from 'react';
import {
  ActionElement,
  ActionProps,
  ActionWithRulesProps,
  defaultOperators,
  defaultValidator,
  formatQuery,
  NotToggle,
  NotToggleProps,
  parseSQL,
  QueryBuilder,
  transformQuery,
} from 'react-querybuilder';
import type { Field, RuleGroupType } from 'react-querybuilder';

import { DataTapeField, typeguards } from '@finley-technologies/finley-types';

import type { ExclusionSqlClauseState } from '../../types';
import { colors } from 'packages/common-frontend/colors';
import {
  fieldValidator,
  validateQuery,
  convertBooleansToString,
  convertStringsToBoolean,
  addLeadingZeroForDecimal,
} from 'packages/common-frontend/components/QueryBuilder';
import 'react-querybuilder/dist/query-builder.scss';
import './querybuilder-styles.scss';

const initialQuery: RuleGroupType = { combinator: 'and', rules: [] };

const stringOnlyOps = [
  'contains',
  'beginsWith',
  'endsWith',
  'doesNotContain',
  'doesNotBeginWith',
  'doesNotEndWith',
];

const generateFieldList = (baseFields: DataTapeField.BaseConfig[]): Field[] => {
  const baseFieldList: Field[] = baseFields.map((baseField) => {
    const defaults = {
      name: `bf.${baseField.field}`,
      label: `bf.${baseField.field}`,
      operators: defaultOperators,
    };
    if (baseField.type === 'boolean' || baseField.type === 'optionalBoolean') {
      return {
        ...defaults,
        operators: defaultOperators.filter((op) => op.name === '='),
        valueEditorType: 'select',
        values: [
          { name: 'TRUE', label: 'TRUE' },
          { name: 'FALSE', label: 'FALSE' },
        ],
        defaultValue: 'TRUE',
        validator: fieldValidator,
      };
    }
    if (baseField.type === 'date' || baseField.type === 'optionalDate') {
      return {
        ...defaults,
        inputType: 'date',
        operators: defaultOperators.filter(
          (op) => !stringOnlyOps.includes(op.name),
        ),
        validator: fieldValidator,
      };
    }
    if (
      baseField.type === 'dateTime' ||
      baseField.type === 'optionalDateTime'
    ) {
      return {
        ...defaults,
        inputType: 'datetime-local',
        operators: defaultOperators.filter(
          (op) => !stringOnlyOps.includes(op.name),
        ),
        validator: fieldValidator,
      };
    }
    if (baseField.type === 'string' || baseField.type === 'optionalString') {
      return {
        ...defaults,
        validator: fieldValidator,
        placeholder: `Enter ${_.lowerCase(baseField.field)}`,
      };
    }
    if (typeguards.creditAgreement.isNumericType(baseField.type)) {
      return {
        ...defaults,
        validator: fieldValidator,
        placeholder: `Enter ${_.lowerCase(baseField.field)}`,
        inputType: 'number',
        operators: defaultOperators.filter(
          (op) => !stringOnlyOps.includes(op.name),
        ),
      };
    }
    // shouldn't reach here
    return defaults;
  });
  const exposureField: Field = {
    name: `rif.exposure`,
    label: `rif.exposure`,
    validator: fieldValidator,
    placeholder: `Enter exposure`,
    inputType: 'number',
    operators: defaultOperators.filter(
      (op) => !stringOnlyOps.includes(op.name),
    ),
  };
  return _.sortBy([...baseFieldList, exposureField], ['name']);
};

const cloneGroupAction = (props: ActionWithRulesProps) => {
  return <ActionElement {...{ ...props, title: 'Duplicate group' }} />;
};

const cloneRuleAction = (props: ActionProps) => {
  return <ActionElement {...{ ...props, title: 'Duplicate rule' }} />;
};

const notToggle = (props: NotToggleProps) => {
  return <NotToggle {...{ ...props, disabled: props.validation !== true }} />;
};

export const ExclusionSqlClauseBuilder = ({
  baseFields,
  loanProductCriteriaIndex,
  initialExclusionSqlClause,
  currentExclusionSqlClauseStates,
  showExclusionSqlClauseRuleErrors,
  readOnly,
}: {
  baseFields: DataTapeField.BaseConfig[];
  loanProductCriteriaIndex: number;
  initialExclusionSqlClause?: string;
  currentExclusionSqlClauseStates: React.MutableRefObject<
    Map<number, ExclusionSqlClauseState>
  >;
  showExclusionSqlClauseRuleErrors: boolean;
  readOnly: boolean;
}) => {
  const [query, setQuery] = useState<RuleGroupType>(() => {
    const currentQueryState = currentExclusionSqlClauseStates.current.get(
      loanProductCriteriaIndex,
    );
    if (currentQueryState) {
      return currentQueryState.exclusionQuery;
    }
    if (initialExclusionSqlClause) {
      return parseSQL(
        addLeadingZeroForDecimal(
          convertBooleansToString(initialExclusionSqlClause),
        ),
      );
    }
    return initialQuery;
  });

  const fieldList: Field[] = useMemo(() => {
    return generateFieldList(baseFields);
  }, [baseFields]);

  return (
    <Flex
      mb="32px"
      padding="24px"
      border="1px"
      borderColor={colors.gray[200]}
      borderRadius="8px"
      direction="column"
    >
      <Text fontSize="md" mb="16px" fontWeight="600">
        Exclude receivables where
      </Text>
      <Flex
        mb="24px"
        padding="16px 24px"
        borderRadius="8px"
        direction="column"
        background={colors.gray[200]}
      >
        <Text>
          {query && query.rules.length > 0
            ? convertStringsToBoolean(
                formatQuery(query, { format: 'sql', parseNumbers: true }),
              )
            : 'The formula will be displayed here.'}
        </Text>
      </Flex>
      <QueryBuilder
        fields={fieldList}
        query={query}
        onQueryChange={(q: RuleGroupType) => {
          const newQuery = transformQuery(q, {
            ruleProcessor: (r) => {
              return {
                ...r,
                value: String(r.value).replace(/['|"]/gi, ''),
              };
            },
          });
          setQuery(newQuery);
          const validationResult = validateQuery(newQuery);
          currentExclusionSqlClauseStates.current.set(
            loanProductCriteriaIndex,
            {
              exclusionSqlClause: convertStringsToBoolean(
                formatQuery(newQuery, { format: 'sql', parseNumbers: true }),
              ),
              exclusionQuery: newQuery,
              isValid: validationResult,
            },
          );
        }}
        addRuleToNewGroups
        showCloneButtons
        showNotToggle
        showCombinatorsBetweenRules
        validator={defaultValidator}
        controlClassnames={{
          queryBuilder: [
            'queryBuilder-branches',
            readOnly ? 'disabled' : undefined,
          ],
          rule: showExclusionSqlClauseRuleErrors ? 'show-errors' : undefined,
        }}
        controlElements={{ cloneGroupAction, cloneRuleAction, notToggle }}
        disabled={readOnly}
      />
    </Flex>
  );
};
